import * as React from 'react';
import { createStyles, makeStyles, Typography } from '@bb-ui/react-library';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { AlertError, Check, Information, Warning } from '@bb-ui/icons/dist/small';
import { useAppConfigContext } from 'contexts/AppConfigContext';

// A banner declared under the `ui:banners` key of a job provider's inputUiSchema.
export interface FormBanner {
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  message: string;
  allowedEnvironments?: string[];
}

export interface FormBannersProps {
  uiSchema?: Record<string, any>;
}

// Banner disables MUI's default icon, so each severity supplies its own.
const severityIcons = {
  error: AlertError,
  warning: Warning,
  info: Information,
  success: Check,
};

export const useStyles = makeStyles((theme) =>
  createStyles({
    banner: {
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(2.5),
      // Override the theme's 32px side padding at md+ to keep the text aligned with the fields.
      '&.MuiAlert-root': {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
      },
    },
    title: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
  }),
);

const isAllowedInStage = (banner: FormBanner, stage: string) =>
  !banner.allowedEnvironments ||
  banner.allowedEnvironments.some((env) => env.toLowerCase() === stage.toLowerCase());

export const FormBanners: React.FC<FormBannersProps> = ({ uiSchema }) => {
  const classes = useStyles();
  const { stage } = useAppConfigContext();

  const banners = React.useMemo<FormBanner[]>(() => {
    const declared = uiSchema?.['ui:banners'];
    if (!Array.isArray(declared)) {
      return [];
    }
    return declared.filter(
      (banner: FormBanner) => banner?.message && isAllowedInStage(banner, stage ?? ''),
    );
  }, [uiSchema, stage]);

  if (banners.length === 0) {
    return null;
  }

  return (
    <div data-testid="job-form-banners">
      {banners.map((banner) => {
        const severity = banner.severity ?? 'warning';
        const Icon = severityIcons[severity] ?? Warning;
        return (
          <Banner
            key={`${banner.title ?? ''}-${banner.message}`}
            className={classes.banner}
            severity={severity}
            icon={<Icon />}
          >
            {banner.title && (
              <Typography variant="body2" className={classes.title}>
                {banner.title}
              </Typography>
            )}
            <Typography variant="body2">{banner.message}</Typography>
          </Banner>
        );
      })}
    </div>
  );
};

export default FormBanners;
